import React from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";

export default function HorizontalGallery({ items, testidPrefix = "gallery-item" }) {
  const trackRef = React.useRef(null);
  const [lightbox, setLightbox] = React.useState(null);
  const [drag, setDrag] = React.useState(null);

  const scrollBy = (dx) => trackRef.current?.scrollBy({ left: dx, behavior: "smooth" });

  React.useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  React.useEffect(() => {
    const h = (e) => {
      if (lightbox === null) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowLeft") setLightbox((l) => Math.max(0, l - 1));
      if (e.key === "ArrowRight") setLightbox((l) => Math.min(items.length - 1, l + 1));
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [lightbox, items.length]);

  // Drag to scroll
  const onDown = (e) => {
    const startX = (e.pageX ?? e.touches?.[0].pageX) - trackRef.current.offsetLeft;
    setDrag({ startX, scrollLeft: trackRef.current.scrollLeft });
  };
  const onMove = (e) => {
    if (!drag) return;
    e.preventDefault?.();
    const x = (e.pageX ?? e.touches?.[0].pageX) - trackRef.current.offsetLeft;
    trackRef.current.scrollLeft = drag.scrollLeft - (x - drag.startX) * 1.2;
  };
  const onUp = () => setDrag(null);

  return (
    <div className="relative">
      <div className="absolute -top-14 right-0 hidden md:flex items-center gap-2">
        <button onClick={() => scrollBy(-360)} className="w-11 h-11 rounded-full border border-brand-border bg-white text-brand-primary hover:bg-brand-primary hover:text-white hover:-translate-y-0.5 transition-all" aria-label="Scroll left" data-testid="gallery-scroll-left">
          <ChevronLeft size={18} className="mx-auto" />
        </button>
        <button onClick={() => scrollBy(360)} className="w-11 h-11 rounded-full border border-brand-border bg-white text-brand-primary hover:bg-brand-primary hover:text-white hover:-translate-y-0.5 transition-all" aria-label="Scroll right" data-testid="gallery-scroll-right">
          <ChevronRight size={18} className="mx-auto" />
        </button>
      </div>

      <div
        ref={trackRef}
        className={`h-scroll pt-2 select-none ${drag ? "cursor-grabbing" : "cursor-grab"}`}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onTouchStart={onDown}
        onTouchMove={onMove}
        onTouchEnd={onUp}
      >
        {items.map((it, i) => (
          <div
            key={it.id || i}
            className="group relative overflow-hidden rounded-2xl shadow-[0_10px_30px_-15px_rgba(0,0,0,0.15)] hover:shadow-[0_25px_50px_-20px_rgba(0,86,166,0.4)] transition-shadow duration-500"
            style={{ width: "360px", height: "440px" }}
          >
            <img
              draggable={false}
              src={it.url}
              alt={it.caption || "Gallery image"}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-brand-dark/20 to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
            <button
              onClick={() => setLightbox(i)}
              className="absolute inset-0 flex items-end p-6 text-left focus:outline-none"
              data-testid={`${testidPrefix}-${i}`}
              aria-label={`Open ${it.caption || "image"}`}
            >
              <div className="w-full">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-glass text-white text-[10px] uppercase tracking-[0.25em] font-semibold border border-white/20">
                  <ZoomIn size={11} /> View
                </div>
                {it.caption && <div className="text-white font-display text-lg font-semibold mt-3 leading-snug">{it.caption}</div>}
              </div>
            </button>
          </div>
        ))}
      </div>

      {lightbox !== null && items[lightbox] && (
        <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-5 right-5 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors" onClick={() => setLightbox(null)} aria-label="Close"><X size={26} /></button>
          <button className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/25 bg-white/5 hover:bg-white/15 text-white flex items-center justify-center" onClick={(e) => { e.stopPropagation(); setLightbox(Math.max(0, lightbox - 1)); }} aria-label="Prev"><ChevronLeft size={22} /></button>
          <button className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/25 bg-white/5 hover:bg-white/15 text-white flex items-center justify-center" onClick={(e) => { e.stopPropagation(); setLightbox(Math.min(items.length - 1, lightbox + 1)); }} aria-label="Next"><ChevronRight size={22} /></button>
          <div className="max-w-[92vw] max-h-[86vh]" onClick={(e) => e.stopPropagation()}>
            <img src={items[lightbox].url} alt={items[lightbox].caption} className="max-h-[80vh] max-w-[92vw] rounded-xl object-contain shadow-2xl" />
            {items[lightbox].caption && <div className="text-center text-white/80 text-sm mt-4 font-light">{items[lightbox].caption}</div>}
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-xs tracking-widest uppercase">{lightbox + 1} / {items.length}</div>
        </div>
      )}
    </div>
  );
}
